/**
 * Order Comparison Engine
 * 
 * Compares existing order items with current cart items to show differences
 * Used in order amendment flow to display changes being made
 */

/**
 * Compare existing order items with current cart items
 * @param {Array} existingItems - Items from existing order (from backend)
 * @param {Array} currentCartItems - Items in current cart (from frontend state)
 * @returns {Object} Object containing categorized changes
 */
export const compareOrderItems = (existingItems, currentCartItems) => {
  alert(`[OrderComparison] Starting comparison:\nExisting items: ${existingItems?.length || 0}\nCurrent items: ${currentCartItems?.length || 0}`);
  
  if (existingItems && existingItems.length > 0) {
    const existingInfo = existingItems.map(item => 
      `${item.name || item.title}: expectedqty=${item.expectedqty}, quantity=${item.quantity}, id=${item.id}`
    ).join('\n');
    alert(`[OrderComparison] Existing Items:\n${existingInfo}`);
  }
  
  const changes = {
    additions: [],      // Completely new items
    increases: [],      // Quantity increases  
    decreases: [],      // Quantity decreases
    removals: [],       // Removed items
    unchanged: [],      // No changes
    summary: {
      hasChanges: false,
      totalAdditions: 0,
      totalIncreases: 0,
      totalDecreases: 0,
      totalRemovals: 0
    }
  };

  // Handle empty arrays safely
  const safeExistingItems = existingItems || [];
  const safeCurrentItems = currentCartItems || [];

  console.log(`[OrderComparison] Safe existing items count: ${safeExistingItems.length}`);
  console.log(`[OrderComparison] Safe current items count: ${safeCurrentItems.length}`);

  // Analyze each cart item vs existing
  safeCurrentItems.forEach(cartItem => {
    console.log(`[OrderComparison] Processing cart item: ${cartItem.title || cartItem.name} (ID: ${cartItem.id}), quantity: ${cartItem.quantity}`);
    
    const existing = safeExistingItems.find(e => {
      // Match by product ID - handle both id and product_id fields
      const match = e.product_id === cartItem.id || e.id === cartItem.id;
      console.log(`[OrderComparison] Checking existing item ID ${e.id || e.product_id} vs cart item ID ${cartItem.id}: ${match ? 'MATCH' : 'no match'}`);
      return match;
    });
    
    if (existing) {
      // Use expectedqty from backend or quantity from frontend, whichever is available
      const existingQuantity = existing.expectedqty || existing.quantity || 0;
      console.log(`[OrderComparison] Found existing item: ${existing.name || existing.title} with quantity ${existingQuantity} (from ${existing.expectedqty ? 'expectedqty' : 'quantity'} field)`);
      
      if (cartItem.quantity > existingQuantity) {
        console.log(`[OrderComparison] QUANTITY INCREASE: ${cartItem.quantity} > ${existingQuantity}`);
      } else if (cartItem.quantity < existingQuantity) {
        console.log(`[OrderComparison] QUANTITY DECREASE: ${cartItem.quantity} < ${existingQuantity}`);
      } else {
        console.log(`[OrderComparison] NO CHANGE: ${cartItem.quantity} == ${existingQuantity}`);
      }
    } else {
      console.log(`[OrderComparison] NEW ITEM: ${cartItem.title} not found in existing order`);
    }
    
    if (!existing) {
      // Completely new item
      changes.additions.push({
        ...cartItem, 
        changeType: 'NEW',
        displayText: `${cartItem.title} x${cartItem.quantity}`,
        icon: '➕'
      });
      changes.summary.totalAdditions += parseInt(cartItem.quantity);
      console.log(`[OrderComparison] Added to ADDITIONS: ${cartItem.title}`);
    } else {
      // Use expectedqty from backend or quantity from frontend, whichever is available
      const existingQuantity = existing.expectedqty || existing.quantity || 0;
      
      if (cartItem.quantity > existingQuantity) {
        // Quantity increase
        const additionalQuantity = cartItem.quantity - existingQuantity;
        changes.increases.push({
          ...cartItem,
          changeType: 'INCREASE', 
          previousQuantity: existingQuantity,
          additionalQuantity: additionalQuantity,
          displayText: `${cartItem.title} +${additionalQuantity} (now x${cartItem.quantity})`,
          icon: '⬆️'
        });
        changes.summary.totalIncreases += additionalQuantity;
        console.log(`[OrderComparison] Added to INCREASES: ${cartItem.title} (+${additionalQuantity})`);
      } else if (cartItem.quantity < existingQuantity) {
        // Quantity decrease
        const reducedQuantity = existingQuantity - cartItem.quantity;
        changes.decreases.push({
          ...cartItem,
          changeType: 'DECREASE', 
          previousQuantity: existingQuantity,
          reducedQuantity: reducedQuantity,
          displayText: `${cartItem.title} -${reducedQuantity} (now x${cartItem.quantity})`,
          icon: '⬇️'
        });
        changes.summary.totalDecreases += reducedQuantity;
        console.log(`[OrderComparison] Added to DECREASES: ${cartItem.title} (-${reducedQuantity})`);
      } else {
        // No change in quantity
        changes.unchanged.push({
          ...cartItem, 
          changeType: 'SAME',
          displayText: `${cartItem.title} x${cartItem.quantity}`,
          icon: '='
        });
        console.log(`[OrderComparison] Added to UNCHANGED: ${cartItem.title}`);
      }
    }
  });
  
  // Find removed items (in existing but not in cart)
  safeExistingItems.forEach(existingItem => {
    const inCart = safeCurrentItems.find(c => 
      c.id === existingItem.product_id || c.id === existingItem.id
    );
    
    if (!inCart) {
      const existingQuantity = existingItem.expectedqty || existingItem.quantity || 0;
      changes.removals.push({
        id: existingItem.product_id || existingItem.id,
        title: existingItem.product_name || existingItem.name,
        quantity: existingQuantity,
        changeType: 'REMOVED',
        displayText: `${existingItem.product_name || existingItem.name} (removed)`,
        icon: '➖'
      });
      changes.summary.totalRemovals += parseInt(existingQuantity);
      console.log(`[OrderComparison] Added to REMOVALS: ${existingItem.product_name || existingItem.name}`);
    }
  });

  // Calculate if there are any changes
  changes.summary.hasChanges = 
    changes.additions.length > 0 ||
    changes.increases.length > 0 ||
    changes.decreases.length > 0 ||
    changes.removals.length > 0;

  alert(`[OrderComparison] RESULTS:\nAdditions: ${changes.additions.length}\nIncreases: ${changes.increases.length}\nDecreases: ${changes.decreases.length}\nRemovals: ${changes.removals.length}\nUnchanged: ${changes.unchanged.length}\nHas changes: ${changes.summary.hasChanges}`);
  
  if (changes.increases.length > 0) {
    const increasesInfo = changes.increases.map(item => 
      `${item.title}: +${item.additionalQuantity}`
    ).join('\n');
    alert(`[OrderComparison] INCREASES:\n${increasesInfo}`);
  }

  return changes;
};

/**
 * Generate a human-readable summary of order changes
 * @param {Object} changes - Result from compareOrderItems
 * @returns {String} Summary text
 */
export const generateChangeSummary = (changes) => {
  if (!changes.summary.hasChanges) {
    return "No changes to existing order";
  }

  const parts = [];
  
  if (changes.summary.totalAdditions > 0) {
    parts.push(`${changes.summary.totalAdditions} new items`);
  }
  
  if (changes.summary.totalIncreases > 0) {
    parts.push(`+${changes.summary.totalIncreases} quantity increases`);
  }
  
  if (changes.summary.totalDecreases > 0) {
    parts.push(`-${changes.summary.totalDecreases} quantity decreases`);
  }
  
  if (changes.summary.totalRemovals > 0) {
    parts.push(`${changes.summary.totalRemovals} items removed`);
  }

  return parts.join(', ');
};

/**
 * Calculate final order items after applying changes
 * @param {Array} existingItems - Original order items
 * @param {Array} currentCartItems - Current cart items
 * @returns {Array} Final order items
 */
export const calculateFinalOrder = (existingItems, currentCartItems) => {
  const finalItems = [];
  const safeCurrentItems = currentCartItems || [];
  
  // Add all current cart items (handles new items and quantity changes)
  safeCurrentItems.forEach(cartItem => {
    finalItems.push({
      id: cartItem.id,
      title: cartItem.title,
      quantity: cartItem.quantity,
      price: cartItem.price,
      // Mark if it's a change from existing
      isModified: true
    });
  });

  return finalItems.sort((a, b) => a.title.localeCompare(b.title));
};